import {
  Button,
  Center,
  HStack,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  type StackProps,
} from "@chakra-ui/react";
import { NavBar } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  NOTIFICATIONS_STORAGE_KEY,
  TASK_NOTIFICATIONS_STORAGE_KEY,
} from "@src/components/modules/notifications/listing/store";
import { Icon } from "@src/components/ui-kit/Icon";
import { NavigationWidgets } from "@src/components/widgets/AppSubNav/navigation-widgets";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { isPathActive } from "@src/utils/routing";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import type { Route } from "nextjs-routes";
import { Children, type ReactNode, useLayoutEffect } from "react";

export const NAV_HEIGHT = 52;

type AppSubNavProps = {
  defaultBackButtonUrl?: Route["pathname"];
  allowedBackButtonUrls?: Route["pathname"][];
  children?: ReactNode;
  showWidgets?: boolean;
  zIndex?: StackProps["zIndex"];
  mobileMenuTitle?: string;
  hideNewTaskButton?: boolean;
};

export const AppSubNav = observer(function AppSubNav({
  allowedBackButtonUrls,
  defaultBackButtonUrl,
  children,
  showWidgets = true,
  zIndex = "421",
  mobileMenuTitle = t`Navigation`,
  hideNewTaskButton,
}: AppSubNavProps) {
  const { taskFormModalStore, taskDetailModalStore, notificationsStore } =
    useStore();
  const { isDesktop } = useScreenType();
  const childrenArray = Children.toArray(children);
  const childCount = childrenArray.length;

  useLayoutEffect(() => {
    notificationsStore.setShowReadNotifications(
      localStorage.getItem(NOTIFICATIONS_STORAGE_KEY) === "true",
    );
    notificationsStore.setShowReadTaskNotifications(
      localStorage.getItem(TASK_NOTIFICATIONS_STORAGE_KEY) === "true",
    );
  }, []);

  return (
    <HStack pos="relative" zIndex={zIndex} justifyContent="space-between">
      <NavBar
        fullWidth
        size={null}
        className="bg-white"
        style={{ height: NAV_HEIGHT }}
      >
        {defaultBackButtonUrl && (
          <NavBar.BackButton
            defaultUrl={defaultBackButtonUrl}
            allowedUrls={allowedBackButtonUrls}
          />
        )}
        {!isDesktop
          ? childCount > 0 && (
              <Menu isLazy>
                <MenuButton
                  as={Button}
                  px="2"
                  fontSize="xs"
                  rightIcon={<Icon name="chevron-down" />}
                  variant="outline"
                >
                  {mobileMenuTitle}
                </MenuButton>
                <Portal>
                  <MenuList>
                    {!hideNewTaskButton && can("task_create_all") && (
                      <Center px="2">
                        <MenuItem
                          as={Button}
                          leftIcon={<Icon name="plus" />}
                          onClick={() =>
                            taskFormModalStore.modalState.onOpen({
                              onSubmit: (task) => {
                                taskDetailModalStore.disclosureState.onOpen({
                                  id: task.id,
                                });
                              },
                            })
                          }
                        >
                          <Trans>New task</Trans>
                        </MenuItem>
                      </Center>
                    )}
                    <MenuGroup title={t`Pages`}>
                      {Children.map(childrenArray, (child, index) => {
                        if (!(child as any)?.props) {
                          return null;
                        }

                        const {
                          children: title,
                          href,
                          active,
                        } = (child as any).props;

                        return (
                          <MenuItem
                            key={title?.toString() ?? index}
                            as={Link}
                            bg={
                              isPathActive(href, active) ? "teal.50" : undefined
                            }
                            href={href ?? ""}
                          >
                            {title ?? ""}
                          </MenuItem>
                        );
                      })}
                    </MenuGroup>
                  </MenuList>
                </Portal>
              </Menu>
            )
          : children}
      </NavBar>
      {showWidgets && <NavigationWidgets appSubNavChildCount={childCount} />}
    </HStack>
  );
});
